<template>
  <div class="edit-event-dialog">
    <v-dialog v-model="open" width="800">
      <v-card class="event-form">
        <v-card-title class="p-3" color="primary">
          <v-icon class="mr-3" @click="close">
            mdi-close
          </v-icon>

          {{ $t('EditEventSeries.EditEventSeries') }}

        </v-card-title>

        <validation-observer ref="form" v-slot="{ invalid }">
          <v-form class="d-flex flex-column p-4">
            <validation-provider v-slot="{ errors }" name="EventName" rules="required|max:125">
              <v-text-field v-model="eventToEdit.name" :counter="125" :error-messages="errors"
                :label="$t('NewCalendarItem.EventName') + '*'"></v-text-field>
            </validation-provider>

            <div class="d-flex">
              <div class="col-md-6 col-xs-12 pl-0 pb-0 pr-5">
                <v-autocomplete :items="eventLocations" item-text="name" v-model="eventToEdit.location"
                  :placeholder="$t('NewCalendarItem.Location')" ref="location" :label="$t('NewCalendarItem.Location')" :search-input.sync="newLocationName">
                  <template v-if="!eventLocations.find(x => x.name === newLocationName)" #append-item="">
                    <v-btn
                      class="ml-2"
                      color="primary"
                      width="96%"
                      @click="addNewLocation()"
                      :disabled="isDisable || !newLocationName"
                    >{{ $t('NewCalendarItem.AddNewLocation') }}</v-btn>
                  </template>
                </v-autocomplete>
              </div>

              <div class="col-md-6 col-xs-12 pl-5 pb-0 pr-0">
                <v-autocomplete
                    class="select-wings"
                    :class="{ 'show-all-wings': isShowAllWings, 'show-append-icon': selectedWingsIds.length > limitSelectedItems }"
                    v-model="selectedWingsIds"
                    :items="wingsLevelOfCare"
                    :label="$t('NewCalendarItem.Wing')"
                    item-text="name"
                    item-value="id"
                    multiple
                    :append-outer-icon="isShowAllWings ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append-outer="openAllWings"
                    :search-input.sync="newWingName"
                >
                  <template v-slot:prepend-item>
                    <v-list-item>
                      <v-list-item-action class="my-1" @click="toggleAllWings">
                        <v-checkbox
                            v-model="isSelectedAllWings"
                            :label="$t('GeneralTranslations.All')"
                        ></v-checkbox>
                      </v-list-item-action>
                    </v-list-item>

                    <v-divider class="mt-2"></v-divider>
                  </template>

                  <template v-slot:item="{ item, on }">
                    <v-list-item class="select-wings-group" v-if="item.group" :key="item.name">
                      <v-list-item-action @click="handleChange(item)">
                        <v-checkbox
                            v-model="selectedGroups[item.levelId]"
                            :label="item.name"
                            :indeterminate="false"
                        ></v-checkbox>
                      </v-list-item-action>
                    </v-list-item>

                    <v-list-item :key="item.name" v-else>
                      <v-list-item-action @click="handleChangeWing(item)">
                        <v-checkbox
                            v-model="selectedWingsIds"
                            :label="item.name"
                            :value="item.id"
                            v-on="on"
                        ></v-checkbox>
                      </v-list-item-action>
                    </v-list-item>
                  </template>

                  <template v-if="!wings.find(x => x.name === newWingName) && isStaffCanCreateWings && !isFacilityHasCareAPI" #append-item="">
                    <v-btn
                        class="ml-2"
                        color="primary"
                        width="94%"
                        @click="addNewWing()"
                        :disabled="isDisableWing || !newWingName"
                    >{{ $t('AdmitResidentsDialog.AddNewWing') }}</v-btn>
                  </template>
                </v-autocomplete>
              </div>
            </div>

            <div class="d-flex">
              <div class="col-md-12 col-xs-12 pr-0 pl-0">
                <v-text-field v-model="eventToEdit.VideoLink"
                  :label="$t('NewCalendarItem.EventYouTubeVideoLink')"></v-text-field>
              </div>
            </div>

            <validation-provider name="Description">
              <v-textarea v-model="eventToEdit.description" :label="$t('GeneralTranslations.Description')"
                rows="1"></v-textarea>
            </validation-provider>

            <div class="d-flex">
              <div class="col-md-4 col-xs-12 d-flex align-items-center text-gray">
                {{ $t('NewCalendarItem.EventDateAndTime') }}
              </div>

              <div class="col-md-8 col-xs-12 p-0 pr-4">
                <div class="d-flex flex-column">
                  <div>
                    <div class="d-flex date-range-wrapper">
                      <v-icon>mdi-calendar</v-icon>
                      <div class="col-12 pr-2">
                        <v-menu v-model="startDateMenu" :close-on-content-click="false" :nudge-right="40"
                          transition="scale-transition" offset-y min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field :value="startDate" :placeholder="$t('NewCalendarItem.RecurringStartDate')"
                              readonly v-bind="attrs" v-on="on" class="date-picker"></v-text-field>
                          </template>

                          <v-date-picker :locale="$i18n.locale" :value="eventToEdit.start" :min="minDate"
                            @input="setStartDate" no-title></v-date-picker>
                        </v-menu>
                      </div>
                    </div>

                    <div class="row mt-4 px-4">
                      <div class="col-6 p-0">
                        <v-menu ref="menu" v-model="pickers.start" :close-on-content-click="false" :nudge-right="40"
                          :return-value.sync="time" transition="scale-transition" offset-y max-width="290px"
                          min-width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="formattedStartTime" :label="$t('NewCalendarItem.StartTime')"
                              prepend-icon="mdi-clock-time-four-outline" readonly :rules="rules.start" v-bind="attrs"
                              v-on="on"></v-text-field>
                          </template>
                          <v-time-picker v-if="pickers.start" v-model="time.start" full-width
                            @click:minute="$refs.menu.save(time)" :format="timeFormat"></v-time-picker>
                        </v-menu>
                      </div>
                      <div class="col-6 p-0">
                        <v-menu ref="menu" v-model="pickers.end" :close-on-content-click="false" :nudge-right="40"
                          :return-value.sync="time" transition="scale-transition" offset-y max-width="290px"
                          min-width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="formattedEndTime" label="End Time"
                              prepend-icon="mdi-clock-time-four-outline" readonly v-bind="attrs" v-on="on"
                              :rules="rules.end"></v-text-field>
                          </template>
                          <v-time-picker v-if="pickers.end" v-model="time.end" full-width
                            @click:minute="$refs.menu.save(time)" :format="timeFormat"></v-time-picker>
                        </v-menu>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="d-flex pt-20-px">
              <div class="col-md-8 col-xs-12 d-flex flex-column pr-0">
                <div class="d-flex">
                  <div class="col-md-7 col-xs-12 text-gray pl-0">
                    <v-checkbox v-model="eventToEdit.isRecurring" :label="$t('NewCalendarItem.RecurringEvent')"
                      :disabled="true" :readonly="true"></v-checkbox>
                  </div>

                  <div class="col-md-5 col-xs-12 p-0">
                    <v-select v-model="eventToEdit.recurringType" :items="recurringTypes" :disabled="true"
                      :readonly="true">
                      <template slot="selection" slot-scope="data">
                        {{ $t(`Calendar.RecurringEventsType.${data.item.text}`) }}
                      </template>
                      <template slot="item" slot-scope="data">
                        {{ $t(`Calendar.RecurringEventsType.${data.item.text}`) }}
                      </template>
                    </v-select>
                  </div>
                </div>

                <div class="d-flex">
                  <div class="col-md-12 col-xs-12 p-0 d-flex align-items-center text-gray">
                    {{ $t('NewCalendarItem.RecurringEndDate') }}
                  </div>
                </div>

                <div class="d-flex">
                  <div class="col-md-12 col-xs-12 p-0">
                    <v-menu v-model="endDateMenu" :close-on-content-click="false" :nudge-right="40"
                      transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field :value="endDate" :placeholder="$t('NewCalendarItem.RecurringEndDate')" readonly
                          v-bind="attrs" v-on="on" class="date-picker"></v-text-field>
                      </template>

                      <v-date-picker :locale="$i18n.locale" :value="eventToEdit.end" @input="setEndDate"
                        no-title></v-date-picker>
                    </v-menu>
                  </div>
                </div>

                <div class="d-flex pt-4" v-if="facility.isEnableVirtualEvents">
                  <div class="col-md-12 col-xs-12 d-flex flex-column p-0">
                    <v-radio-group v-model="eventToEdit.isVirtualEvent" row>
                      <v-radio :label="$t('NewCalendarItem.VirtualEvent')" :value="true"></v-radio>
                      <v-radio :label="$t('NewCalendarItem.NotVirtualEvent')" :value="false"></v-radio>
                    </v-radio-group>

                    <v-row class="external-host" v-if="eventToEdit.isVirtualEvent">
                      <v-checkbox v-model="eventToEdit.isExternallyHosted"
                                  :label="$t('NewCalendarItem.AddAnExternalPersonAsHost')"></v-checkbox>
                      <div class="external-host-inner" v-show="eventToEdit.isExternallyHosted">
                        <v-text-field v-model="eventToEdit.hostEmailAddress" :rules="eventToEdit.isExternallyHosted ? rules.email : false
                          " class="pa-2" :label="$t('NewCalendarItem.ExternalHostEmailAddress')"
                                      append-icon="mdi-close" @click:append="eventToEdit.hostEmailAddress = ''"></v-text-field>
                      </div>
                    </v-row>
                  </div>
                </div>
              </div>

              <div class="col-md-4 col-xs-12 pr-0">
                <v-card class="h-100">
                    <div class="position-relative d-flex justify-content-center align-items-center h-100 image-upload">
                    <img :src="eventToEdit.imageUrl" alt="" class="h-100 w-100" />

                    <div v-if="loading" class="position-absolute top-0 left-0">
                      <v-progress-circular :size="100" color="primary" indeterminate>
                      </v-progress-circular>
                    </div>
                  </div>
                  <div class="gallery-photos-actions d-flex justify-content-end pt-1">
                    <v-btn class="mr-1" icon @click="openGalleryPhotos()">
                      <v-icon color="#3F51B5">mdi-image-multiple</v-icon>
                    </v-btn>

                    <v-btn icon @click="$refs.inputUpload.click()">
                      <v-icon color="#3F51B5">mdi-camera</v-icon>
                    </v-btn>
                    <input v-show="false" ref="inputUpload" type="file" @change="saveEventImage" />
                  </div>
                </v-card>
              </div>
            </div>


            <div class="text-gray">{{ $t('GeneralTranslations.StarIndicatesRequiredField') }}</div>

            <div class="pt-2">

              <div class="inline-block">
                <v-btn class="mr-3" color="primary" :disabled="invalid" outlined @click.stop="onEventSeriesDelete">
                  {{ $t('EditEventSeries.Delete') }}
                </v-btn>
              </div>

              <template v-if="eventToEdit.isPublished">
                <div class="inline-block float-right">
                  <v-btn :disabled="invalid" class="btn-default ml-4" @click="openScheduldEventsDialog">
                    {{ $t('NewCalendarItem.SaveAsDraft') }}
                  </v-btn>
                </div>
                <div class="inline-block float-right">
                  <v-btn :disabled="invalid" class="btn-default ml-4" @click="publishEvent">
                    {{ $t('GeneralTranslations.SaveChanges') }}
                  </v-btn>
                </div>
              </template>
              <template v-else>
                <div class="inline-block float-right">
                  <v-btn :disabled="invalid" class="btn-default ml-4" @click="publishEvent">
                    {{ $t('NewCalendarItem.Publish') }}
                  </v-btn>
                </div>
                <div class="inline-block float-right">
                  <v-btn :disabled="invalid" class="btn-default ml-4" @click="openScheduldEventsDialog">
                    {{ $t('NewCalendarItem.SaveAsDraft') }}
                  </v-btn>
                </div>
              </template>
            </div>
          </v-form>
        </validation-observer>
      </v-card>
    </v-dialog>

    <ConfirmDlg ref="confirmationModal"></ConfirmDlg>
    <ScheduledPublishEventsDialog v-model="isOpenScheduldEventsDialog" :data="{scheduledPublishDateTime: eventToEdit.scheduledPublishDateTime }" @onScheduldEventsDialogResponse="onScheduldEventsDialogResponse"/>
    <GalleryPhotosUploadDlg ref="GalleryPhotosUploadDlg" @uploadGalleryPhoto="uploadGalleryPhoto"/>
  </div>
</template>

<script>
import firebase from "firebase";
import { v4 as uuidv4 } from "uuid";
import { mapState } from "vuex";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import "@/plugins/vee-validate";
import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import { RecurringEventsType } from "@/enums/RecurringEventsType";
import moment from "moment";
import { uploadImage } from "@/utils/upload-image";
import { ValidationRules } from "@/utils/validation-rules";
import { convertTimeDurationTo24hFormat, setTimeForDate } from '@/utils/dateTimeFormat';
import { EventLocationsMixin } from "@/mixins/EventLocations.ts";
import { toTranslationKey } from "@/utils/stringFormat";
import ScheduledPublishEventsDialog from "@/views/calender/view-calendar-item/ScheduledPublishEventsDialog.vue";
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import {waitResizedImage} from "@/utils/wait-resized-image";

export default {
  components: {
    ScheduledPublishEventsDialog,
    ValidationProvider,
    ValidationObserver,
    ConfirmDlg,
    GalleryPhotosUploadDlg
  },
  mixins: [EventLocationsMixin],
  props: {
    event: {
      type: Object,
      default: () => ({})
    },
    value: Boolean
  },
  data() {
    return {
      eventToEdit: { ...this.event },
      isOpenScheduldEventsDialog: false,
      loading: false,
      saving: false,
      startDateMenu: false,
      endDateMenu: false,
      minDate: moment(new Date()).format("YYYY-MM-DD"), //today
      timeFormat: '',
      recurringTypes: Object.values(RecurringEventsType).map(x => ({ text: toTranslationKey(x), value: x })),

      pickers: {
        start: false,
        end: false
      },
      time: {
        start: null,
        end: null
      }
    };
  },

  created() {
    this.eventToEdit.scheduledPublishDateTime = this.eventToEdit.scheduledPublishDateTime? moment(this.eventToEdit.scheduledPublishDateTime.seconds * 1000).toDate() : null;
    this.$store.dispatch("getCalenderRecurringEvents", this.eventToEdit.id);
  },

  mounted() {
    this.time.start = moment(this.eventToEdit.start).format("HH:mm");
    this.time.end = moment(this.eventToEdit.end).format("HH:mm");
    this.timeFormat = this.facility.timeFormat.includes('A') ? 'ampm' : '24h';
    if(this.eventToEdit?.wingsIds.length) {
      this.selectedWingsIds = this.eventToEdit.wingsIds;
    }
  },

  computed: {
    ...mapState({
      calendarRecurringEvents: state =>
        state.calenderModule.currentCalenderRecurringEvents,
      recurringEventsUpdated: state =>
        state.calenderModule.recurringEventsUpdated,
      facility: state => state.facilityModule.selectedFacility,
      currentFacility: state => state.authModule.currentFacility
    }),
    recurringType() {
      return RecurringEventsType;
    },
    startDate() {
      return this.eventToEdit && this.eventToEdit.start
        ? moment(this.eventToEdit.start).format('ddd, ' + this.facility.dateFormat)
        : moment(new Date()).format('ddd, ' + this.facility.dateFormat);
    },
    endDate() {
      return this.eventToEdit && this.eventToEdit.end
        ? moment(this.eventToEdit.end).format('ddd, ' + this.facility.dateFormat)
        : moment(new Date()).format('ddd, ' + this.facility.dateFormat);
    },
    rules() {
      return {
        start: [ValidationRules.required],
        end: [
          ValidationRules.required,
          () => {
            const start = moment(this.time.start, "h:mma");
            const end = moment(this.time.end, "h:mma");
            return (
              moment(end).isAfter(start) ||
              "Event end date cannot be before start date"
            );
          }
        ]
      };
    },
    formattedStartTime() {
      if (!this.facility.timeFormat) return ''

      return moment(this.time.start, 'HH:mm').format(this.facility.timeFormat)
    },
    formattedEndTime() {
      if (!this.facility.timeFormat) return ''

      return moment(this.time.end, 'HH:mm').format(this.facility.timeFormat)
    },
    open: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  },
  watch: {
    recurringEventsUpdated(newValue) {
      //detect updating series and update list of events
      if (newValue.length > 0) {
        this.$emit("afterUpdate", newValue);
        this.saving = false;
      }
    },
    calendarRecurringEvents(newValue) {
      if (!newValue) return;

      const firstEvent = this.getUpcomingRecurringEvents()[0];
      if (firstEvent) {
        this.eventToEdit.start = moment(firstEvent.start).toISOString();
        this.eventToEdit.end = moment(
          this.calendarRecurringEvents[
            this.calendarRecurringEvents.length - 1
          ].end
        ).toISOString();
      }
    },
    "time.start"(start, prevStart) {
      if (prevStart != null) {
        this.time.end = moment(start, "h:mma")
          .add(1, "h")
          .format("HH:mm");
      }
    },
    selectedWingsIds(newValue) {
      this.eventToEdit.wingsIds = newValue;
    }
  },
  methods: {
    onScheduldEventsDialogResponse(response) {
      this.saveAsDraft(response.isScheduled === true ? response.scheduledPublishDateTime : null)
      this.closeScheduldEventsDialog()
    },

    getUpcomingRecurringEvents() {
      const now = moment(new Date());

      if (!this.calendarRecurringEvents) return [];

      const upcoming = this.calendarRecurringEvents.filter(
        x => moment(x.end) >= now
      );

      return upcoming;
    },

    setStartDate(date) {
      const time = moment(this.eventToEdit.start).format("HH:mm");
      const startDate = moment(date).toDate()
      setTimeForDate(startDate, time)
      this.eventToEdit.start = startDate

      this.startDateMenu = false;
    },
    setEndDate(date) {
      const time = moment(this.eventToEdit.end).format("HH:mm");
      const endDate = moment(date).toDate()
      setTimeForDate(endDate, time)
      this.eventToEdit.end = endDate

      this.endDateMenu = false;
    },
    saveEventImage(e) {
      const path = `${this.currentFacility}/events/coverImages/${uuidv4()}`;
      const file = e.target.files[0];

      uploadImage({
        path,
        file,
        onStart: () => {
          this.loading = true;
        },
        onComplete: async storage => {
          const uploadedImageUrl = await storage.snapshot.ref.getDownloadURL();
          const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);

          try {
            const resizedImageUrl = await waitResizedImage(10, resizedImageRef);
            this.eventToEdit.imageUrl = uploadedImageUrl;
            this.eventToEdit.thumbnailUrl = resizedImageUrl;
          } catch (e) {
            this.eventToEdit.imageUrl = uploadedImageUrl;
            this.eventToEdit.thumbnailUrl = uploadedImageUrl;
          }

          this.loading = false;
        }
      });
    },

    async updateEvent(isPublished, showEditQuestion, scheduledPublishDateTime) {

      let response = null

      if (showEditQuestion === true) {
        response = await this.$refs.confirmationModal.open(null, this.$t("EditPremiumService.EditEventSeriesMessage"), false)
      }
      else {
        response = { agree: true }
      }

      if (response && response.agree) {
        this.saving = true;

        const event = { ...this.eventToEdit }
        event.isPublished = isPublished

        if (isPublished) {
          event.scheduledPublishDateTime = null
        }

        event.scheduledPublishDateTime = scheduledPublishDateTime || null

        const eventData = {
          event: event,
          recurringRange: {
            duration: `${this.time.start}-${this.time.end}`,
            start: moment(event.start).format("YYYY-MM-DD"),
            end: moment(event.end).format("YYYY-MM-DD")
          }
        };
        this.$emit("onEventSeriesUpdate", eventData);
      }
    },
    async saveAsDraft(scheduledPublishDateTime) {
      if (this.eventToEdit.isPublished) {
        await this.$refs.confirmationModal
          .open(null, this.$t('ViewCalendarItem.UnPublishAllEventsQuestion'), false)
          .then(async response => {
            if (response && response.agree) {
              await this.updateEvent(false, false, scheduledPublishDateTime)
            }
          });
      }
      else {
        await this.updateEvent(false, true, scheduledPublishDateTime)
      }
    },
    publishEvent() {
      this.updateEvent(true, true, null)
    },
    close() {
      this.$refs.confirmationModal
        .open(null, this.$i18n.messages[this.$i18n.locale]['EditEventSeries.CancelEditsMessage'], false)
        .then(response => {
          if (response && response.agree) {
            this.closeDialog();
          }
        });
    },
    onEventSeriesDelete() {
      this.$emit("deleteEventSeries", this.eventToEdit.parentEventId);
    },
    closeDialog() {
      this.open = false;
    },
    openScheduldEventsDialog() {
      this.isOpenScheduldEventsDialog = true
    },
    closeScheduldEventsDialog() {
      this.isOpenScheduldEventsDialog = false
    },
    openGalleryPhotos() {
      this.$refs.GalleryPhotosUploadDlg.open();
    },
    uploadGalleryPhoto(image) {
      this.eventToEdit.imageUrl = image.image;
      this.eventToEdit.thumbnailUrl = image?.thumbnailUrl ?? image.image;
    }
  }
};
</script>

<style scoped lang="scss">
.block-bottom {
  position: absolute;
  bottom: 0;
  width: 100%;
}
::v-deep.gallery-photos-actions .v-btn__content {
  padding: 0 !important;
}
</style>
